import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FiUsers, FiArrowLeft, FiSearch, FiCheck, FiAlertCircle, FiX,
  FiChevronUp, FiChevronDown
} from 'react-icons/fi';
import { 
  Table, Button, Modal, Form, Input, Select, Space, message, Popconfirm, 
  Tooltip, Badge, Typography, Avatar, Switch
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  LockOutlined, 
  SaveOutlined, 
  CloseOutlined,
  CheckOutlined  // Add this line
} from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;
const { Option } = Select;

const UserManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser, authToken } = useAuth();
  
  // State management
  const [loading, setLoading] = useState({
    users: false,
    departments: false,
    roles: false,
    permissions: false,
    submitting: false
  });
  
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  
  // Form instance
  const [form] = Form.useForm();

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(prev => ({ ...prev, users: true }));
      const response = await axios.get('/api/users', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      console.log('Fetched users data:', response.data);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Failed to load users');
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      setLoading(prev => ({ ...prev, departments: true }));
      const response = await axios.get('/api/departments', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      message.error('Failed to load departments');
    } finally {
      setLoading(prev => ({ ...prev, departments: false }));
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    try {
      setLoading(prev => ({ ...prev, roles: true }));
      const response = await axios.get('/api/roles', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      // Filter out manager and admin roles for non-admin users
      const isAdmin = currentUser && (currentUser.role === 'admin' || currentUser.role_name === 'admin');
      const filteredRoles = response.data.filter(role => {
        if (isAdmin) {
          // Admins can see all roles except manager
          return role.name !== 'manager';
        } else {
          // Department admins cannot see manager or admin roles
          return role.name !== 'manager' && role.name !== 'admin';
        }
      });
      setRoles(filteredRoles);
    } catch (error) {
      console.error('Error fetching roles:', error);
      message.error('Failed to load roles');
    } finally {
      setLoading(prev => ({ ...prev, roles: false }));
    }
  };

  // Handle edit user
  const handleEdit = (user) => {
    console.log('Editing user:', user);
    console.log('Current user:', currentUser);
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      phone: user.phone || '',
      departmentId: user.department_id,
      roleId: user.role_id,
      isActive: user.is_active
    });
    setIsModalVisible(true);
  };

  // Handle delete user
  const handleDelete = async (userId) => {
    try {
      await axios.delete(`/api/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  // Handle form submit
  const handleSubmit = async (values) => {
    try {
      setLoading(prev => ({ ...prev, submitting: true }));
      
      if (editingUser) {
        // Update existing user
        await axios.put(`/api/users/${editingUser.id}`, values, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        message.success('User updated successfully');
      } else {
        // Create new user
        await axios.post('/api/users', values, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        message.success('User created successfully');
      }
      
      setIsModalVisible(false);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      message.error(error.response?.data?.message || 'Failed to save user');
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  };

  // Initialize data
  useEffect(() => {
    if (authToken) {
      console.log('Current user from auth context:', currentUser);
      fetchUsers();
      fetchDepartments();
      fetchRoles();
    }
  }, [authToken, currentUser]);

  // Table columns
  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.first_name || record.last_name 
              ? `${record.first_name || ''} ${record.last_name || ''}`.trim()
              : record.username}
            {record.is_superuser && (
              <Tooltip title="Super Administrator">
                <LockOutlined style={{ marginLeft: 8, color: '#ff4d4f' }} />
              </Tooltip>
            )}
          </div>
          <div style={{ color: '#666' }}>@{record.username}</div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>
            <MailOutlined style={{ marginRight: 8 }} />
            {record.email}
          </div>
          {record.phone && (
            <div>
              <PhoneOutlined style={{ marginRight: 8 }} />
              {record.phone}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Department',
      dataIndex: 'department_name',
      key: 'department',
      render: (text) => text || <span style={{ color: '#999' }}>Not assigned</span>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Badge 
          status={record.is_active ? 'success' : 'default'} 
          text={record.is_active ? 'Active' : 'Inactive'} 
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isEditDisabled = record.is_superuser && !currentUser?.is_superuser;
        console.log(`User ${record.username}: is_superuser=${record.is_superuser}, currentUser.is_superuser=${currentUser?.is_superuser}, editDisabled=${isEditDisabled}`);
        console.log('Record data:', record);
        
        return (
          <Space size="middle">
            <Button 
              type="primary" 
              size="small"
              onClick={() => handleEdit(record)}
              disabled={false} // Temporarily disable the permission check
              title="Edit User"
            >
              ✏️ Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this user?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              disabled={record.is_superuser}
            >
              <Button 
                type="primary" 
                danger
                size="small"
                icon={<DeleteOutlined />} 
                disabled={record.is_superuser}
                title="Delete User"
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Filter users based on search text
  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchText.toLowerCase()) ||
    user.email.toLowerCase().includes(searchText.toLowerCase()) ||
    (user.first_name && user.first_name.toLowerCase().includes(searchText.toLowerCase())) ||
    (user.last_name && user.last_name.toLowerCase().includes(searchText.toLowerCase()))
  );

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Header and search section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 sm:mb-6 gap-4">
        <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 text-gray-600 hover:text-gray-800 flex-shrink-0"
            title="Back to Dashboard"
          >
            <FiArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">User Management</h1>
            <p className="text-sm sm:text-base text-gray-600 hidden sm:block">Manage system users and their permissions</p>
          </div>
        </div>
        <button
          onClick={() => {
            setEditingUser(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
          className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-sm text-sm sm:text-base"
        >
          <FiUsers className="mr-2" />
          <span className="hidden sm:inline">Add New User</span>
          <span className="sm:hidden">Add User</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="mb-4 sm:mb-6 bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search users..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            dataSource={filteredUsers}
            loading={loading.users}
            pagination={{ pageSize: 10, responsive: true }}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            locale={{
            emptyText: (
              <div className="py-8 text-center">
                <FiUsers className="mx-auto h-12 w-12 opacity-30" />
                <p className="mt-2 text-sm font-medium text-gray-500">
                  {searchText ? 'No users match your search' : 'No users found'}
                </p>
                {searchText && (
                  <button
                    onClick={() => setSearchText('')}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )
          }}
        />
        </div>
      </div>

      {/* Add/Edit User Modal */}
      <Modal
        title={
          <div className="flex items-center">
            {editingUser ? (
              <>
                <EditOutlined className="mr-2" />
                Edit User
              </>
            ) : (
              <>
                <UserOutlined className="mr-2" />
                Add New User
              </>
            )}
          </div>
        }
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isActive: true
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true, message: 'Please input first name!' }]}
            >
              <Input placeholder="First Name" />
            </Form.Item>

            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true, message: 'Please input last name!' }]}
            >
              <Input placeholder="Last Name" />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input placeholder="Email" prefix={<MailOutlined />} />
          </Form.Item>

          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Please input username!' }]}
          >
            <Input 
              placeholder="Username" 
              prefix={<UserOutlined />} 
              disabled={!!editingUser}
            />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: !editingUser, message: 'Please input password!' },
                { min: 6, message: 'Password must be at least 6 characters!' }
              ]}
            >
              <Input.Password placeholder="Password" />
            </Form.Item>
          )}

          <Form.Item
            name="phone"
            label="Phone Number"
          >
            <Input placeholder="Phone" prefix={<PhoneOutlined />} />
          </Form.Item>

          <Form.Item
            name="roleId"
            label="Role"
          >
            <Select
              showSearch
              placeholder="Select role"
              optionFilterProp="children"
              loading={loading.roles}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {roles.map(role => (
                <Option key={role.id} value={role.id}>
                  {role.name} - {role.description}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="departmentId"
            label="Department"
          >
            <Select
              showSearch
              placeholder="Select department"
              optionFilterProp="children"
              loading={loading.departments}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {departments.map(dept => (
                <Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              defaultChecked
            />
          </Form.Item>

          <div className="flex justify-end space-x-3 mt-6">
            <Button
              onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading.submitting}
              icon={<SaveOutlined />}
            >
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;